import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid"
import { useEffect, useRef } from "react"
import { generateDaysOfMonth, getDaysAroundDate } from "./utils"
import { IEnkCalendarViewProps } from "./calendar-types"
import { EnkCalendarDay } from "./calender-day"
import { EnkCalendarMonth } from "./calendar-month"
import { addMonths } from "date-fns"

export default function EnkDayView(props: IEnkCalendarViewProps) {
  const { selectedDate, setSelectedDate, currentLocale } = props
  const container = useRef<HTMLDivElement>(null)
  const containerNav = useRef<HTMLDivElement>(null)
  const containerOffset = useRef<HTMLDivElement>(null)

  const days = generateDaysOfMonth(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate)
  const monthName = selectedDate.toLocaleString(currentLocale, { month: "long" })
  const headerDays = getDaysAroundDate(selectedDate, 3, 3)

  const handleDayClick = (day: Date) => {
    setSelectedDate(day)
  }

  useEffect(() => {
    // Set the container scroll position based on the current time.
    const currentMinute = new Date().getHours() * 60
    if (container.current && containerNav.current && containerOffset.current) {
      container.current.scrollTop =
        ((container.current.scrollHeight - containerNav.current.offsetHeight - containerOffset.current.offsetHeight) * currentMinute) / 1440
    }
  }, [])

  const handleMonthChange = (step: number) => {
    setSelectedDate(addMonths(selectedDate, step))
  }

  return (
    <div>
      <div className="isolate flex flex-auto overflow-hidden bg-white">
        <div ref={container} className="flex flex-auto flex-col overflow-auto">
          <div
            ref={containerNav}
            className="sticky top-0 z-10 grid flex-none grid-cols-7 bg-white text-xs text-gray-500 shadow ring-1 ring-black ring-opacity-5 md:hidden"
          >
            {/* Small view of days */}
            {headerDays.map((day) => (
              <button type="button" className="flex flex-col items-center pb-1.5 pt-3" onClick={() => handleDayClick(new Date(day.date))}>
                <span>{new Date(day.date).toLocaleString(currentLocale, { weekday: "short" }).charAt(0).toUpperCase()}</span>
                <EnkCalendarDay day={day} />
              </button>
            ))}
          </div>
          <div className="flex w-full flex-auto">
            <div className="w-14 flex-none bg-white ring-1 ring-gray-100" />
            <div className="grid flex-auto grid-cols-1 grid-rows-1">
              {/* Horizontal lines */}
              <div
                className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
                style={{ gridTemplateRows: "repeat(48, minmax(3.5rem, 1fr))" }}
              >
                {/* 24 hours */}
                <div ref={containerOffset} className="row-end-1 h-7"></div>
                {[...Array(24).keys()].map((hour) => (
                  <>
                    <div>
                      <div key={hour} className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                        {hour.toString().padStart(2, "0")}:00
                      </div>
                    </div>
                    <div />
                  </>
                ))}
              </div>

              {/* Events */}
              <ol
                className="col-start-1 col-end-2 row-start-1 grid grid-cols-1"
                style={{ gridTemplateRows: "1.75rem repeat(288, minmax(0, 1fr)) auto" }}
              >
                <li className="relative mt-px flex" style={{ gridRow: "74 / span 12" }}>
                  <a
                    href="#"
                    className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg bg-blue-50 p-2 text-xs leading-5 hover:bg-blue-100"
                  >
                    <p className="order-1 font-semibold text-blue-700">Breakfast</p>
                    <p className="text-blue-500 group-hover:text-blue-700">
                      <time dateTime="2022-01-22T06:00">6:00 AM</time>
                    </p>
                  </a>
                </li>
                <li className="relative mt-px flex" style={{ gridRow: "92 / span 30" }}>
                  <a
                    href="#"
                    className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg bg-pink-50 p-2 text-xs leading-5 hover:bg-pink-100"
                  >
                    <p className="order-1 font-semibold text-pink-700">Flight to Paris</p>
                    <p className="order-1 text-pink-500 group-hover:text-pink-700">John F. Kennedy International Airport</p>
                    <p className="text-pink-500 group-hover:text-pink-700">
                      <time dateTime="2022-01-22T07:30">7:30 AM</time>
                    </p>
                  </a>
                </li>
                <li className="relative mt-px flex" style={{ gridRow: "134 / span 18" }}>
                  <a
                    href="#"
                    className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg bg-indigo-50 p-2 text-xs leading-5 hover:bg-indigo-100"
                  >
                    <p className="order-1 font-semibold text-indigo-700">Sightseeing</p>
                    <p className="order-1 text-indigo-500 group-hover:text-indigo-700">Eiffel Tower</p>
                    <p className="text-indigo-500 group-hover:text-indigo-700">
                      <time dateTime="2022-01-22T11:00">11:00 AM</time>
                    </p>
                  </a>
                </li>
              </ol>
            </div>
          </div>
        </div>
        {/* right side month calendar */}
        <div className="hidden w-1/2 max-w-md flex-none border-l border-gray-100 px-8 py-10 md:block">
          <div className="flex items-center text-center text-gray-900">
            <button
              type="button"
              className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              onClick={() => handleMonthChange(-1)}
            >
              <span className="sr-only">Vorige maand</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <div className="flex-auto text-sm font-semibold">
              {monthName} {selectedDate.getFullYear()}
            </div>
            <button
              type="button"
              className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              onClick={() => handleMonthChange(1)}
            >
              <span className="sr-only">Volgende maand</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <EnkCalendarMonth days={days} {...props} />
        </div>
      </div>
    </div>
  )
}
